/* Phase 2 primitive: the one HMS empty state.
 *
 * What a table, list or search result shows when it has nothing to show.
 * Screens keep rebuilding this as an ad-hoc centred div, so the icon
 * size, the copy hierarchy and the spacing drift apart screen by screen.
 *
 * Props:
 *  - icon:      ReactNode slot — a FontAwesomeIcon, an inline SVG, an
 *               emoji; rendered at 48px in the secondary text colour
 *  - title:     the one-line "nothing here" statement
 *  - hint:      the smaller second line — what to do about it
 *  - action:    ReactNode slot; callers pass an HMSButton
 *  - variant:   'default' is transparent, for dropping INSIDE a card or
 *               a table body that already has its own surface.
 *               'card' brings its own surface, border and shadow, for
 *               standing alone on a page.
 *  - className: extra hook
 *
 * Every colour here is a theme-aware token, so dark mode needs no rules
 * of its own — the file is on the darkmode generator's EXCLUDE list
 * precisely because there is nothing for it to generate.
 */
import React from 'react';
import './HMSEmptyState.css';

const HMSEmptyState = ({
  icon,
  title,
  hint,
  action,
  variant = 'default',
  className = '',
}) => {
  const cls = [
    'hms-empty',
    variant === 'card' ? 'hms-empty-card' : '',
    className,
  ].filter(Boolean).join(' ');

  return (
    <div className={cls}>
      {icon && <div className="hms-empty-icon" aria-hidden="true">{icon}</div>}
      {/* A <p>, not a heading: an empty state is a state of the panel it
          sits in, and injecting an h3 here would punch a hole in the
          page's heading outline. */}
      {title && <p className="hms-empty-title">{title}</p>}
      {hint && <p className="hms-empty-hint">{hint}</p>}
      {action && <div className="hms-empty-action">{action}</div>}
    </div>
  );
};

export default HMSEmptyState;
